import { ImpersonationBanner, IMPERSONATION_BANNER_OFFSET_PX } from "@/components/platform/ImpersonationBanner";
import { PlatformShell } from "@/components/platform/PlatformShell";
import { WorkspaceShell } from "@/components/shell/WorkspaceShell";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AccountWorkspaceLayout, AccountWorkspaceProvider } from "@/lib/account-workspace";
import { AuthProvider, useAuth } from "@/lib/auth";
import { MeDialogProvider } from "@/lib/me-dialog";
import AccountAuditLogPage from "@/pages/AccountAuditLogPage";
import AccountDetailPage from "@/pages/AccountDetailPage";
import AccountOnboardingPage from "@/pages/AccountOnboardingPage";
import AssigneesPage from "@/pages/AssigneesPage";
import AuthorizationsPage from "@/pages/AuthorizationsPage";
import BrandHubPage from "@/pages/BrandHubPage";
import EnterprisePage from "@/pages/EnterprisePage";
import InviteAcceptPage from "@/pages/InviteAcceptPage";
import KbAuditPage from "@/pages/kb/KbAuditPage";
import KbWorkbenchPage from "@/pages/kb/KbWorkbenchPage";
import MaterialsPage from "@/pages/kb/MaterialsPage";
import OpsPlanPlaceholderPage from "@/pages/brand/OpsPlanPlaceholderPage";
import BrandSettingsBasicPage from "@/pages/brand/BrandSettingsBasicPage";
import BrandSettingsMembersPage from "@/pages/brand/BrandSettingsMembersPage";
import LockedPage from "@/pages/LockedPage";
import LoginPage from "@/pages/LoginPage";
import MagicLoginPage from "@/pages/MagicLoginPage";
import MePage from "@/pages/MePage";
import MembersPage from "@/pages/MembersPage";
import PlatformAccountsPage from "@/pages/platform/PlatformAccountsPage";
import PlatformBrandsPage from "@/pages/platform/PlatformBrandsPage";
import PlatformTemplatesPage from "@/pages/platform/PlatformTemplatesPage";
import PlatformTemplateIdePage from "@/pages/platform/PlatformTemplateIdePage";
import PlatformUsersPage from "@/pages/platform/PlatformUsersPage";
import ProductAppPage from "@/pages/ProductAppPage";
import JourneyCustomerChatDeepLink from "@/pages/journey/JourneyCustomerChatDeepLink";
import JourneyCustomersPage from "@/pages/journey/JourneyCustomersPage";
import JourneyConfigPage from "@/pages/journey/JourneyConfigPage";
import JourneyEditPage from "@/pages/journey/JourneyEditPage";
import JourneyLayout from "@/pages/journey/JourneyLayout";
import JourneyExecutionPage from "@/pages/journey/JourneyExecutionPage";
import JourneyOverviewPage from "@/pages/journey/JourneyOverviewPage";
import JourneySettingsPage from "@/pages/journey/JourneySettingsPage";
import BrandSiteLayout from "@/pages/brand-site/BrandSiteLayout";
import BrandSiteLegacyRedirect, {
  BrandSiteListRedirect,
} from "@/pages/brand-site/BrandSiteLegacyRedirect";
import BrandSiteListPage from "@/pages/brand-site/BrandSiteListPage";
import BrandSiteSiteLayout from "@/pages/brand-site/BrandSiteSiteLayout";
import BrandSiteOverviewPage from "@/pages/brand-site/BrandSiteOverviewPage";
import BrandSiteEditPage from "@/pages/brand-site/BrandSiteEditPage";
import BrandSitePagesPage from "@/pages/brand-site/BrandSitePagesPage";
import BrandSitePublishPage from "@/pages/brand-site/BrandSitePublishPage";
import BrandSiteDomainPage from "@/pages/brand-site/BrandSiteDomainPage";
import BrandSiteIndexingPage from "@/pages/brand-site/BrandSiteIndexingPage";
import RootRedirectPage from "@/pages/RootRedirectPage";
import SubscriptionsPage from "@/pages/SubscriptionsPage";
import WecomConnectionPage from "@/pages/account/WecomConnectionPage";
import WecomSidebarPage from "@/pages/wecom/WecomSidebarPage";
import AccountChannelsPage from "@/pages/account/AccountChannelsPage";
import BrandConnectorsPage from "@/pages/brand/BrandConnectorsPage";
import { Navigate, Outlet, Route, Routes, useParams } from "react-router";
import type { ReactNode } from "react";

function AppToaster() {
  const { impersonation } = useAuth();
  return (
    <Toaster
      position="top-center"
      offset={impersonation ? IMPERSONATION_BANNER_OFFSET_PX + 12 : 16}
    />
  );
}

function AppShell({ children }: { children: ReactNode }) {
  const { impersonation } = useAuth();
  return (
    <div
      className="flex h-svh max-h-svh flex-col overflow-hidden"
      style={
        {
          "--impersonation-banner-offset": impersonation
            ? `${IMPERSONATION_BANNER_OFFSET_PX}px`
            : "0px",
        } as React.CSSProperties
      }
    >
      <ImpersonationBanner />
      <div className="min-h-0 flex-1 overflow-hidden">{children}</div>
    </div>
  );
}

/** 旧会话深链 → 客户深链 */
function ConversationsContactRedirect() {
  const { contactId } = useParams();
  return <Navigate to={`../customers/${contactId}`} replace />;
}

function RequireAuth() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center text-muted-foreground">
        正在确认登录状态…
      </main>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  return <Outlet />;
}

/** Account workspace chrome — only when the user has an account context. */
function ShellLayout() {
  return (
    <AccountWorkspaceProvider>
      <MeDialogProvider>
        <WorkspaceShell>
          <Outlet />
        </WorkspaceShell>
      </MeDialogProvider>
    </AccountWorkspaceProvider>
  );
}

function ReceivedAuthorizationsRedirect() {
  const { accountId } = useParams();
  return <Navigate to={`/accounts/${accountId}/authorizations?tab=received`} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <TooltipProvider>
        <AppShell>
          <Routes>
          <Route path="/wecom-sidebar" element={<WecomSidebarPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/login/magic" element={<MagicLoginPage />} />
          <Route path="/invitations/:token" element={<InviteAcceptPage />} />
          <Route element={<RequireAuth />}>
            <Route path="/" element={<RootRedirectPage />} />
            <Route path="/onboarding" element={<AccountOnboardingPage />} />
            <Route path="/accounts/new" element={<Navigate to="/onboarding" replace />} />
            <Route path="/platform" element={<PlatformShell />}>
              <Route index element={<Navigate to="users" replace />} />
              <Route path="users" element={<PlatformUsersPage />} />
              <Route path="accounts" element={<PlatformAccountsPage />} />
              <Route path="brands" element={<PlatformBrandsPage />} />
              <Route path="templates" element={<PlatformTemplatesPage />} />
              <Route path="templates/:sourceKey/ide" element={<PlatformTemplateIdePage />} />
            </Route>
            <Route element={<ShellLayout />}>
              <Route path="/accounts/:accountId" element={<AccountWorkspaceLayout />}>
                <Route index element={<EnterprisePage />} />
                <Route path="settings" element={<AccountDetailPage />} />
                <Route path="brands" element={<Navigate to=".." relative="path" replace />} />
                <Route path="brands/:brandId" element={<BrandHubPage />} />
                <Route path="brands/:brandId/plan" element={<OpsPlanPlaceholderPage />} />
                {/* kb/* 单实例：index 与深链切页不 remount 整页工作台 */}
                <Route path="brands/:brandId/kb/inbox" element={<Navigate to="../inbox" relative="path" replace />} />
                <Route path="brands/:brandId/kb/*" element={<KbWorkbenchPage />} />
                <Route path="brands/:brandId/inbox" element={<KbWorkbenchPage />} />
                <Route path="brands/:brandId/materials" element={<MaterialsPage />} />
                <Route
                  path="brands/:brandId/sites/:siteId/materials"
                  element={<MaterialsPage />}
                />
                <Route path="brands/:brandId/brand_site" element={<BrandSiteLayout />}>
                  <Route index element={<BrandSiteListPage />} />
                </Route>
                <Route path="brands/:brandId/connectors" element={<BrandConnectorsPage />} />
                <Route
                  path="brands/:brandId/settings"
                  element={<Navigate to="basic" replace />}
                />
                <Route path="brands/:brandId/settings/basic" element={<BrandSettingsBasicPage />} />
                <Route
                  path="brands/:brandId/settings/members"
                  element={<BrandSettingsMembersPage />}
                />
                {/* brand_site / journey 须写在通用 apps/:appKey 之前 */}
                <Route path="brands/:brandId/apps/brand_site" element={<BrandSiteLayout />}>
                  <Route index element={<BrandSiteListRedirect />} />
                  <Route path="overview" element={<BrandSiteListRedirect />} />
                  <Route path="edit" element={<BrandSiteLegacyRedirect section="edit" />} />
                  <Route path="pages" element={<BrandSiteLegacyRedirect section="pages" />} />
                  <Route path="publish" element={<BrandSiteLegacyRedirect section="publish" />} />
                  <Route path="domain" element={<BrandSiteLegacyRedirect section="domain" />} />
                  <Route path="sites/:siteId" element={<BrandSiteSiteLayout />}>
                    <Route index element={<Navigate to="overview" replace />} />
                    <Route path="overview" element={<BrandSiteOverviewPage />} />
                    <Route path="edit" element={<BrandSiteEditPage />} />
                    <Route path="pages" element={<BrandSitePagesPage />} />
                    <Route path="publish" element={<BrandSitePublishPage />} />
                  <Route path="indexing" element={<BrandSiteIndexingPage />} />
                    <Route path="domain" element={<BrandSiteDomainPage />} />
                  </Route>
                </Route>
                <Route path="brands/:brandId/apps/journey" element={<JourneyLayout />}>
                  <Route index element={<Navigate to="overview" replace />} />
                  <Route path="overview" element={<JourneyOverviewPage />} />
                  <Route path="execution" element={<JourneyExecutionPage />} />
                  {/* 旧旅程列表入口 → 旅程配置 */}
                  <Route path="journeys" element={<Navigate to="../settings/journeys" replace />} />
                  <Route path="journeys/new" element={<JourneyEditPage />} />
                  <Route path="journeys/:journeyId" element={<JourneyEditPage />} />
                  <Route path="customers" element={<JourneyCustomersPage />} />
                  <Route path="customers/:contactId" element={<JourneyCustomerChatDeepLink />} />
                  {/* 旧「会话」入口已并入客户；保留重定向以免书签失效 */}
                  <Route path="conversations" element={<Navigate to="../customers" replace />} />
                  <Route path="conversations/:contactId" element={<ConversationsContactRedirect />} />
                  <Route path="settings" element={<Navigate to="journeys" replace />} />
                  <Route path="settings/journeys" element={<JourneyConfigPage />} />
                  <Route path="settings/members" element={<JourneySettingsPage />} />
                </Route>
                <Route path="brands/:brandId/apps/:appKey" element={<ProductAppPage />} />
                <Route path="brands/:brandId/apps/:appKey/:section" element={<ProductAppPage />} />
                <Route
                  path="brands/:brandId/apps/:appKey/:section/:subsection"
                  element={<ProductAppPage />}
                />
                <Route path="members" element={<MembersPage />} />
                <Route path="subscriptions" element={<SubscriptionsPage />} />
                <Route path="wecom" element={<WecomConnectionPage />} />
                <Route path="channels" element={<AccountChannelsPage />} />
                <Route path="kb/audit" element={<KbAuditPage />} />
                <Route path="kb/inbox" element={<Navigate to="../inbox" relative="path" replace />} />
                <Route path="kb/*" element={<KbWorkbenchPage />} />
                <Route path="inbox" element={<KbWorkbenchPage />} />
                <Route path="materials" element={<MaterialsPage />} />
                <Route path="authorizations" element={<AuthorizationsPage />} />
                <Route path="received-authorizations" element={<ReceivedAuthorizationsRedirect />} />
                <Route path="received-authorizations/:authorizationId/assignees" element={<AssigneesPage />} />
                <Route path="audit-log" element={<AccountAuditLogPage />} />
              </Route>
              <Route path="/me" element={<MePage />} />
              <Route path="/locked" element={<LockedPage />} />
              {/* Legacy redirects */}
              <Route path="/accounts" element={<Navigate to="/" replace />} />
              <Route path="/authorizations" element={<Navigate to="/" replace />} />
              <Route path="/authorizations/:id/assignees" element={<Navigate to="/" replace />} />
            </Route>
          </Route>
        </Routes>
        <AppToaster />
        </AppShell>
      </TooltipProvider>
    </AuthProvider>
  );
}
